/**
 * React Query hooks for municipality roles CRUD.
 *
 * Phase 2 – USER_ROLES.md
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchMunicipalityRoles,
  fetchMunicipalityRole,
  createMunicipalityRole,
  updateMunicipalityRole,
} from '../services/roles';
import type {
  MunicipalityRoleCreateInput,
  MunicipalityRoleUpdateInput,
} from '@municipality/types';

export const rolesKeys = {
  all: ['municipalityRoles'] as const,
  list: (includeArchived?: boolean) => [...rolesKeys.all, 'list', { includeArchived }] as const,
  detail: (roleId: string) => [...rolesKeys.all, 'detail', roleId] as const,
};

const STALE_TIME = 2 * 60 * 1000;

export function useMunicipalityRoles(options?: { includeArchived?: boolean }) {
  return useQuery({
    queryKey: rolesKeys.list(options?.includeArchived),
    queryFn: () => fetchMunicipalityRoles(options),
    staleTime: STALE_TIME,
  });
}

export function useMunicipalityRole(roleId: string | null) {
  return useQuery({
    queryKey: rolesKeys.detail(roleId ?? ''),
    queryFn: () => (roleId ? fetchMunicipalityRole(roleId) : Promise.resolve(null)),
    enabled: !!roleId,
    staleTime: STALE_TIME,
  });
}

export function useCreateMunicipalityRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      input,
      createdBy,
    }: { input: MunicipalityRoleCreateInput; createdBy?: string }) =>
      createMunicipalityRole(input, createdBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.all });
    },
  });
}

export function useUpdateMunicipalityRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, input }: { roleId: string; input: MunicipalityRoleUpdateInput }) =>
      updateMunicipalityRole(roleId, input),
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.all });
      queryClient.invalidateQueries({ queryKey: rolesKeys.detail(roleId) });
    },
  });
}
